import React, { ReactNode, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { List, Map } from 'immutable';
import { useHistory } from 'react-router-dom';
import { ColProps } from 'react-flexbox-grid';
import { subDays } from 'date-fns';
import { AxiosResponse } from 'axios';
import { Badge } from '@saucelabs/ds-badge';
import { Breadcrumbs } from '@saucelabs/ds-breadcrumbs';
import { Option, Select } from '@saucelabs/ds-inputs';
import { LinkButton } from '@saucelabs/ds-link';
import { ContentLoader, Loader } from '@saucelabs/sl-component-loader';
import { actions as authActions } from '@saucelabs/sl-container-authentication';
import { Typography } from '@saucelabs/ds-typography';
import { Col, Row } from '@saucelabs/sl-component-grid';
import {
  IconAndroidFill,
  IconArrowLeftFill,
  IconAutomatedOutline,
  IconCheckmarkCircleFill,
  IconIosFill,
  IconMacosFill,
  IconQuestionOutline,
  IconWindowsFill,
} from '@saucelabs/ds-icons';
import { OverlayTrigger, Tooltip } from '@saucelabs/sl-component-overlay';
import { records as userRecords, OrgPlansNotFetchedError } from '@saucelabs/sl-module-user';

import BackToPlatformLink from '../components/BackToPlatformLink';
import { EnterprisePlansTile } from '../components/Purchase/ProductTiles/EnterprisePlansTile';
import {
  ALLOWED_CREDIT_BLOCK_VALUES,
  CREDITS_PER_BLOCK,
  CREDIT_BLOCKS_KEY,
  INTERVAL_KEY,
  PLAN_INTERVAL,
  PRODUCT_USAGE,
  ROUTE_PARENT_SECTION,
  ROUTE_SECTION_CHECKOUT,
} from '../constants';
import { PlanConfiguration } from '../records/PlanConfiguration';
import { Plan } from '../records/Plan';
import { Subscription } from '../records/Subscription';
import { MonthlySummaries, monthlySummaries } from '../utils/burns-calculations';
import { amountSeparator, formatThousand } from '../utils/numbers';
import { suggestedCreditBlocksToBuy } from '../utils/plans';
import { selectors as plansSelectors, utils as plansUtils } from '../ducks/plans';
import { selectors as subscriptionsSelectors } from '../ducks/subscriptions';
import { actions, operations, selectors } from '../ducks/purchase';
import { trackEvent, EVENTS, MODE_USAGE } from '../tracking';
import { PlanPurchaseCard } from '../components/PlanPurchaseCard';
import { FeatureList } from '../components/Plans/FeatureList';
import FeatureListStyles from '../components/Plans/FeatureList.styles';
import Styles from './UsagePurchaseContainer.styles';
import { CreditsRatesTable } from '../components/CreditsRatesTable';
import { HowCreditsWorkFAQ } from '../components/HowCreditsWorkFAQ';
import { MoreTestingPlans } from '../components/Plans/MoreTestingPlans';
import { usePlanConfigManager } from '../utils/usePlanConfigManager';

const CARD_CONFIG: ColProps = {
  xs: 12,
  lg: 4,
  className: 'my-2 lg:my-0',
};

const PARENT_CRUMB = {
  name: 'Subscription and Billing',
  url: `/${ROUTE_PARENT_SECTION}`,
  onClick: () => trackEvent(EVENTS.PLANS_NAVIGATE_BACK, { checkout_step: 'overview' }),
};

const QUESTION_ICON_CLASSES = `w-2 h-2 text-medium-emphasis align-baseline fill-current text-medium-emphasis ${FeatureListStyles.tooltip}`;

interface AlsoIncludesItem {
  content: string,
  tooltip?: React.ReactNode
}

interface AlsoIncludeCategory {
  id: string,
  items: AlsoIncludesItem[],
  category?: string
}

const SUBHEADING_COPY = Object.freeze({
  [PLAN_INTERVAL.MONTH]: 'The flexibility of paying month to month.',
  [PLAN_INTERVAL.YEAR]: 'More speed and credits that last longer.',
});
const INCLUDES_COPY = Object.freeze({
  [PLAN_INTERVAL.MONTH]: [
    'Run tests on up to 25 devices in parallel',
    'Thousands of real and virtual devices',
    'Unlimited users',
  ],
  [PLAN_INTERVAL.YEAR]: [
    <Typography
      className="font-semibold"
      component="span"
    >
      Run tests on up to 50 devices in parallel
    </Typography>,
    'Thousands of real and virtual devices',
    'Unlimited users',
  ],
});
const ALSO_INCLUDE_COPY: AlsoIncludeCategory[] = [
  {
    id: 'col_1',
    items: [
      { content: 'Debugging tools' },
      { content: 'Videos & screenshots of tests' },
      { content: 'Rest API access' },
    ],
  },
  {
    id: 'col_2',
    items: [
      { content: 'Secure local tunneling' },
      { content: 'Secure, single use devices and VMs' },
      {
        content: 'Email support',
        tooltip: (
          <>
            Customers get the following support:
            <ul className="text-left m-0 p-2">
              <li>
                E-mail and online support for all issues and questions,
                12:00am – 5:00pm Pacific Time
              </li>
              <li>Initial Response Times: 1 Business Day for all issues</li>
              <li>24 hour system monitoring</li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    id: 'col_3',
    category: 'Access additional testing features (no credits required)',
    items: [
      { content: 'API Testing: 5 projects and 15K executions/mo', tooltip: 'Automated test generation. Fast debugging. Secure connections.' },
      { content: 'Mobile Beta Testing: 10 apps, 100 beta testers', tooltip: 'Optimize your testing and app development processes.' }],
  },
];

const iconClasses = 'text-xs h-2 w-2 align-top fill-current text-gray-500 mt-0.25 mr-1';

export const PLANS_ICON_MAP: Record<string, JSX.Element> = Object.freeze({
  'Windows PCs': <IconWindowsFill className={iconClasses} />,
  macOS: <IconMacosFill className={iconClasses} />,
  'Android Emulators': <IconAndroidFill className={iconClasses} />,
  'iOS Simulators': <IconIosFill className={iconClasses} />,
  'Android Smartphones & Tablets': <IconAndroidFill className={iconClasses} />,
  'iOS Smartphones & Tablets': <IconIosFill className={iconClasses} />,
});

export interface CommonUsagePurchaseProps {
  clearPurchaseData: () => void,
  fetchOrgPlans: () => void,
  getMonthlyUsage: ({ endDate: Date, periods: number }) => Promise<Array<number>>,
  isEmbedded?: boolean
  orderItems: Map<string, PlanConfiguration>;
  persistPurchaseData: () => void,
  updateProductConfig: (any) => void,
  usagePlans: { [interval: string]: Plan },
  usageSubscriptions: List<Subscription>,
  user: userRecords.UserRecord
}

export interface Props extends CommonUsagePurchaseProps {
  apiTestingPlans: { [interval: string]: Plan },
  initialized: boolean,
  mobileQualityPlans: { [interval: string]: Plan },
  usageSubscriptions: List<Subscription>,
}

export const UsagePurchaseSection: React.FC<CommonUsagePurchaseProps> = ({
  clearPurchaseData,
  fetchOrgPlans,
  getMonthlyUsage,
  isEmbedded,
  orderItems,
  persistPurchaseData,
  updateProductConfig,
  usagePlans,
  usageSubscriptions,
  user,
}) => {
  const history = useHistory();

  const usageSubscription = usageSubscriptions.first(); // Customers should not have more than one
  const isUpgrading = usageSubscriptions.size > 0;
  const currentSubscriptionBlocks = usageSubscription?.getCreditBlocksAmount() || 0;

  const [usageSummary, setUsageSummary] = useState<MonthlySummaries>(Object);
  const [localUsageConfig, setLocalUsageConfig] = useState({
    [PLAN_INTERVAL.MONTH]: {
      [CREDIT_BLOCKS_KEY]: 1,
      [INTERVAL_KEY]: PLAN_INTERVAL.MONTH,
      product: PRODUCT_USAGE,
      totalCredits: CREDITS_PER_BLOCK,
    },
    [PLAN_INTERVAL.YEAR]: {
      bonusCredits: plansUtils.annualBonusCredits(12),
      [CREDIT_BLOCKS_KEY]: 12,
      [INTERVAL_KEY]: PLAN_INTERVAL.YEAR,
      product: PRODUCT_USAGE,
      totalCredits: 12 * CREDITS_PER_BLOCK,
    },
  });

  useEffect(() => {
    if (isUpgrading) {
      // If the user is upgrading (already has a usage subscription), we fetch the credits burnt
      // in the last 3 full billing periods to display average and peak usage
      const endDate = subDays(new Date(usageSubscription.current_period_started_at), 1);
      void getMonthlyUsage({ endDate, periods: 3 })
        .then((result: AxiosResponse<MonthlyBurnResponse>) => {
          const summaries = monthlySummaries(result.data.burn_monthly);
          setUsageSummary(summaries);
          const suggestedBlocks = suggestedCreditBlocksToBuy(
            currentSubscriptionBlocks,
            summaries.average,
          );
          // We preselect the number of credit blocks to be higher than average usage
          setLocalUsageConfig({
            ...localUsageConfig,
            [PLAN_INTERVAL.MONTH]: {
              ...localUsageConfig[PLAN_INTERVAL.MONTH],
              [CREDIT_BLOCKS_KEY]: suggestedBlocks.monthly,
            },
            [PLAN_INTERVAL.YEAR]: {
              ...localUsageConfig[PLAN_INTERVAL.YEAR],
              [CREDIT_BLOCKS_KEY]: suggestedBlocks.annual,
            },
          });
        });
    }
  }, [
    currentSubscriptionBlocks,
    getMonthlyUsage,
    isUpgrading,
    setLocalUsageConfig,
    usageSubscription?.current_period_started_at,
  ]);

  let isFreemiumCapable;
  try {
    if (!usageSubscription) {
      isFreemiumCapable = user?.isFreemiumCapable();
    }
  } catch (error) {
    if (error instanceof OrgPlansNotFetchedError) {
      // This is needed so that some user methods like isFreemiumCapable can be called
      // in this container or any of the children
      fetchOrgPlans();
      return (
        <Loader
          className="m-auto my-16"
          fontSize="4em"
        />
      );
    }
    throw error;
  }

  const handleDropdownChange = (dropdownValue: string, interval: string): void => {
    const creditBlocks = Number(dropdownValue);

    trackEvent(EVENTS.USAGE_PLANS_SELECT_BLOCKS, {
      ...MODE_USAGE,
      [CREDIT_BLOCKS_KEY]: creditBlocks,
      [INTERVAL_KEY]: interval,
    });

    clearPurchaseData();

    // Dropdown value is always a string, hence converting to number
    setLocalUsageConfig({
      ...localUsageConfig,
      [interval]: {
        ...localUsageConfig[interval],
        [CREDIT_BLOCKS_KEY]: creditBlocks,
        bonusCredits: plansUtils.annualBonusCredits(creditBlocks),
        totalCredits: creditBlocks * CREDITS_PER_BLOCK,
      },
    });
  };

  const proceedWithUsagePlan = (interval: PlanInterval) => {
    trackEvent(EVENTS.PLANS_CONTINUE, {
      ...MODE_USAGE,
      [INTERVAL_KEY]: interval,
      [PRODUCT_USAGE]: orderItems.get(PRODUCT_USAGE)?.[CREDIT_BLOCKS_KEY] || 0,
    });
    updateProductConfig(localUsageConfig[interval]);
    persistPurchaseData();
    history.push(`/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_CHECKOUT}`);
  };

  // Renders the selected option in the tile (after it's selected)
  const renderPlansSelectedItem = (selectedItem, interval): ReactNode => {
    if (!selectedItem) {
      return 'Select Plan';
    }

    const planPrice = (
      <Typography
        className={selectedItem.rendered[0].props.className}
        component={selectedItem.rendered[0].props.component}
        variant="body-large"
      >
        {selectedItem.rendered[0].props.children}
      </Typography>
    );

    if (interval === PLAN_INTERVAL.YEAR) {
      const bonusElement = (
        <span className="text-green-600 text-sm">
          {`(${formatThousand(plansUtils.annualBonusCredits(selectedItem.key), 'standard')} bonus)`}
        </span>
      );
      return [planPrice, bonusElement];
    }

    if (selectedItem.index === 0) {
      const starterPlanBadge = (
        <Badge
          className="active mr-1"
          color="green"
          emphasis="high"
        >
          Starter Plan
        </Badge>
      );

      return [starterPlanBadge, planPrice];
    }
    return [planPrice];
  };

  const monthlyPlan: Plan | undefined = usagePlans[PLAN_INTERVAL.MONTH];
  const annualPlan: Plan | undefined = usagePlans[PLAN_INTERVAL.YEAR];

  return (
    <>
      <div
        className={`${isEmbedded ? 'max-w-154' : 'max-w-140'} mx-auto mb-2`}
        data-qa="usage-purchase-section"
      >
        {isUpgrading && (
          <div className="flex justify-between items-center mt-6">
            <Typography
              className="text-high-emphasis m-0"
              disableSpacing
              variant="heading-1"
            >
              Update Plan
            </Typography>
            <LinkButton
              onPress={() => {
                history.push(`/${ROUTE_PARENT_SECTION}`);
              }}
              prefixIcon={() => <IconArrowLeftFill className="flex fill-current w-2 h-2" />}
            >
              <span className="text-base font-semibold">
                Go Back
              </span>
            </LinkButton>
          </div>
        )}
        <Typography
          className="text-high-emphasis text-center m-0 mt-5"
          disableSpacing
          variant="heading-2"
        >
          <IconAutomatedOutline className={`${Styles['heading-icon']} fill-current text-gray-500 mr-1 h-4 w-auto`} />
          Automated + Live Testing Plans
        </Typography>
        <Row>
          <Col
            data-qa="update-plan-subheading"
            xs={12}
          >
            {isUpgrading ? (
              <div className="flex flex-col items-center mt-3 mb-4">
                <Loader show={!Object.prototype.hasOwnProperty.call(usageSummary, 'peak')}>
                  {usageSummary.peak > currentSubscriptionBlocks * CREDITS_PER_BLOCK ? (
                    <>
                      <Typography
                        className="text-high-emphasis text-center m-0"
                        disableSpacing
                        variant="heading-5"
                      >
                        Your usage summary:
                      </Typography>
                      <ul className="mt-2 mb-0">
                        {usageSummary.average > currentSubscriptionBlocks * CREDITS_PER_BLOCK && (
                          <li>
                            {`Your average usage: ${formatThousand(usageSummary.average, 'standard')} credits/month (last ${usageSummary.monthsUsedForAverage} months)`}
                          </li>
                        )}
                        <li>
                          {`Your peak usage: ${formatThousand(usageSummary?.peak, 'standard')} credits (${usageSummary?.peakPeriodName})`}
                        </li>
                      </ul>
                    </>
                  ) : (
                    <Typography
                      variant="body-large"
                    >
                      Choose the right plan for your needs:
                    </Typography>
                  )}
                </Loader>
              </div>
            ) : (
              <Typography
                className="text-high-emphasis text-center m-0 mt-3 mb-4"
                disableSpacing
                variant="heading-3"
              >
                Buy credits for testing on real and virtual devices
                <Typography
                  className="text-white align-middle ml-1 px-1 bg-red-600 rounded-full inline-block"
                  disableSpacing
                  variant="body-small"
                >
                  New
                </Typography>
              </Typography>
            )}
          </Col>
        </Row>
        <Row>
          <Col {...CARD_CONFIG}>
            {usageSubscription && (
              <PlanPurchaseCard
                billingPeriod={usageSubscription.getInterval()}
                forceMonthlyPricing={usageSubscription.isAnnual()}
                heading={`Your Plan (${usageSubscription.getIntervalLabel()})`}
                includes={[
                  usageSubscription.isMonthly() ?
                    `${formatThousand(usageSubscription.getCreditBlocksAmount() * CREDITS_PER_BLOCK, 'standard')} credits per month. Credits expire monthly.` :
                    `${formatThousand(usageSubscription.getCreditBlocksAmount() * CREDITS_PER_BLOCK, 'standard')} credits + ${plansUtils.annualBonusCredits(usageSubscription.getCreditBlocksAmount()).toLocaleString()} bonus credits per year. Credits expire annually.`,
                  ...INCLUDES_COPY[usageSubscription.getInterval()],
                ]}
                isCurrentPlan
                rate={usageSubscription.getMonthlyPrice()}
                subheading={SUBHEADING_COPY[usageSubscription.getInterval()]}
                taglines={[
                  `${formatThousand(usageSubscription.getCreditBlocksAmount() * CREDITS_PER_BLOCK, 'standard')} credits/${usageSubscription.getInterval()}`,
                ]}
                variant="elevation"
              />
            )}
            {!usageSubscription && isFreemiumCapable && (
              <PlanPurchaseCard
                heading="Free"
                includes={[
                  '2,000 free credits per week to run Automated and Live Tests. Credits expire weekly.',
                  'Thousands of mobile devices, emulators/simulators, and browser/OS combinations',
                  'Unlimited users',
                ]}
                isCurrentPlan
                rate={0}
                subheading="Free weekly credits to get started."
                taglines={[
                  '2,000 free credits per week',
                  'Run tests on up to 10 devices in parallel',
                ]}
                variant="elevation"
              />
            )}
            {!usageSubscription && !isFreemiumCapable && (
              <PlanPurchaseCard
                heading="Free 28-day trial"
                includes={[
                  '160 testing minutes to run Automated and Live Tests.',
                  'Thousands of mobile devices, emulators/simulators, and browser/OS combinations',
                  'Unlimited users',
                ]}
                isCurrentPlan={user.isFreeUser()}
                rate={0}
                subheading="Free testing minutes to try Sauce."
                taglines={[
                  '160 testing minutes',
                  'Run tests on up to 2 devices in parallel',
                ]}
                variant="elevation"
              />
            )}
          </Col>
          <Col {...CARD_CONFIG}>
            <PlanPurchaseCard
              billingPeriod={PLAN_INTERVAL.MONTH as PlanInterval}
              dropdown={(
                <Select
                  className="mt-1"
                  data-qa="usage-purchase-dropdown"
                  onSelectionChange={(val) => handleDropdownChange(
                    val as string, PLAN_INTERVAL.MONTH)}
                  renderSelectedItem={(selectedItem) => (
                    renderPlansSelectedItem(selectedItem, PLAN_INTERVAL.MONTH)
                  )}
                  selectedKey={localUsageConfig[PLAN_INTERVAL.MONTH][CREDIT_BLOCKS_KEY].toString()}
                  selectionMode="single"
                >
                  {ALLOWED_CREDIT_BLOCK_VALUES[PLAN_INTERVAL.MONTH].map((blocks: number) => (
                    <Option
                      key={`${blocks}`}
                      data-qa={`usage-purchase-option-${blocks}`}
                      isDisabled={isUpgrading && blocks === currentSubscriptionBlocks}
                      textValue={`${blocks * CREDITS_PER_BLOCK} credits/month - ${blocks * (monthlyPlan?.getPriceForCreditBlock() || 0)}/month`}
                    >
                      <Typography
                        className="text-high-emphasis mr-0.5"
                        component="span"
                        variant="body-small"
                      >
                        {(blocks * CREDITS_PER_BLOCK).toLocaleString()}
                        {' '}
                        credits/month
                      </Typography>
                      {' '}
                      <Typography
                        className="text-medium-emphasis"
                        component="span"
                        variant="body-small"
                      >
                        -
                        {' '}
                        $
                        {blocks * (monthlyPlan?.getPriceForCreditBlock() || 0)}
                        /month
                      </Typography>
                    </Option>
                  ))}
                </Select>
              )}
              handleContinue={() => proceedWithUsagePlan(PLAN_INTERVAL.MONTH as PlanInterval)}
              heading="Monthly"
              includes={[
                <Typography
                  className="font-semibold"
                  component="span"
                >
                  {localUsageConfig[PLAN_INTERVAL.MONTH].totalCredits.toLocaleString()}
                  {' credits per month. Credits expire monthly.'}
                </Typography>,
                ...INCLUDES_COPY[PLAN_INTERVAL.MONTH],
              ]}
              rate={monthlyPlan?.calculateMonthlyRate(
                localUsageConfig[PLAN_INTERVAL.MONTH][CREDIT_BLOCKS_KEY],
              )}
              subheading={SUBHEADING_COPY[PLAN_INTERVAL.MONTH]}
              taglines={[
                <Typography
                  className="font-bold"
                  component="span"
                >
                  Run tests on up to 25 devices in parallel
                </Typography>,
              ]}
              variant="elevation"
            />
          </Col>
          <Col {...CARD_CONFIG}>
            <PlanPurchaseCard
              billingPeriod={PLAN_INTERVAL.YEAR as PlanInterval}
              dropdown={(
                <Select
                  className="mt-1"
                  data-qa="usage-purchase-dropdown"
                  onSelectionChange={(value) => handleDropdownChange(
                    value as string, PLAN_INTERVAL.YEAR)}
                  renderSelectedItem={(selectedItem) => (
                    renderPlansSelectedItem(selectedItem, PLAN_INTERVAL.YEAR)
                  )}
                  selectedKey={localUsageConfig[PLAN_INTERVAL.YEAR][CREDIT_BLOCKS_KEY].toString()}
                  selectionMode="single"
                >
                  {ALLOWED_CREDIT_BLOCK_VALUES[PLAN_INTERVAL.YEAR].map((blocks: number) => (
                    <Option
                      key={`${blocks}`}
                      data-qa={`usage-purchase-option-${blocks}`}
                      isDisabled={isUpgrading && blocks === currentSubscriptionBlocks}
                      textValue={`${blocks * CREDITS_PER_BLOCK + plansUtils.annualBonusCredits(blocks)} credits/year - ${blocks * (annualPlan?.getPriceForCreditBlock() || 0)}/year`}
                    >
                      <Typography
                        className="text-high-emphasis mr-0.5"
                        component="span"
                        variant="body-small"
                      >
                        {(blocks * CREDITS_PER_BLOCK + plansUtils.annualBonusCredits(blocks))
                          .toLocaleString()}
                        {' '}
                        credits/year
                      </Typography>
                      {' - '}
                      <Typography
                        className="text-medium-emphasis"
                        component="span"
                        variant="body-small"
                      >
                        {`${amountSeparator(blocks * (annualPlan?.getPriceForCreditBlock() || 0))}`}
                        /year
                      </Typography>
                    </Option>
                  ))}
                </Select>
              )}
              handleContinue={() => proceedWithUsagePlan(PLAN_INTERVAL.YEAR as PlanInterval)}
              heading="Annual"
              includes={[
                <Typography
                  className="font-semibold"
                  component="span"
                >
                  {localUsageConfig[PLAN_INTERVAL.YEAR].totalCredits.toLocaleString()}
                  {' credits + '}
                  {localUsageConfig[PLAN_INTERVAL.YEAR].bonusCredits?.toLocaleString()}
                  {' bonus credits per year. Credits expire annually.'}
                </Typography>,
                ...INCLUDES_COPY[PLAN_INTERVAL.YEAR],
              ]}
              rate={annualPlan?.calculateMonthlyRate(
                localUsageConfig[PLAN_INTERVAL.YEAR][CREDIT_BLOCKS_KEY],
              )}
              savings="Best value - 10% credit bonus"
              subheading={SUBHEADING_COPY[PLAN_INTERVAL.YEAR]}
              taglines={[
                <Typography
                  className="font-bold"
                  component="span"
                >
                  Run tests on up to 50 devices in parallel
                </Typography>,
              ]}
              variant="elevation"
            />
          </Col>
        </Row>
      </div>
      <div className="mx-auto max-w-76 px-2">
        <Typography
          className="text-high-emphasis text-center mt-5 mb-2"
          variant="heading-3"
        >
          Rates for automated + manual tests
        </Typography>
        <Typography
          className="text-center mb-2"
          variant="body-large"
        >
          Credits are applied automatically when you run tests
        </Typography>
        <CreditsRatesTable />
      </div>
      <div className="mt-5 pt-2 pb-5 bg-white">
        <Typography
          className="text-center mb-3 text-high-emphasis"
          disableSpacing
          variant="heading-3"
        >
          All plans above also include
        </Typography>
        <div className="max-w-screen-xl mx-auto px-2 grid grid-rows-1 grid-cols-1 lg:grid-cols-3">
          {ALSO_INCLUDE_COPY.map((cat) => (
            <div
              key={`${cat.id}`}
              className="grid grid-cols-1"
            >
              {cat.category && (
                <Typography
                  className="text-high-emphasis font-semibold mb-0.5"
                  component="span"
                  variant="body-large"
                >
                  {cat.category}
                </Typography>
              )}
              <FeatureList
                isColumn
                marginless
              >
                {cat.items.map((item) => (
                  <li
                    key={item.content}
                    className="flex items-start my-0.5"
                  >
                    <IconCheckmarkCircleFill className="mt-0.5 flex-shrink-0 fill-current text-green-600 text-base" />
                    <Typography
                      className="text-high-emphasis"
                      component="span"
                      variant="body-large"
                    >
                      {item.content}
                      {item.tooltip && (
                        <>
                          {' '}
                          <OverlayTrigger
                            delayHide={300}
                            overlay={(
                              <Tooltip id="also_includes_tooltip">
                                {item.tooltip}
                              </Tooltip>
                            )}
                            placement="top"
                          >
                            <IconQuestionOutline className={QUESTION_ICON_CLASSES} />
                          </OverlayTrigger>
                        </>
                      )}
                    </Typography>
                  </li>
                ))}
              </FeatureList>
            </div>
          ))}
        </div>
      </div>
      <HowCreditsWorkFAQ collapsible />
    </>
  );
};

const UsagePurchaseContainer: React.FC<Props> = ({
  apiTestingPlans,
  clearPurchaseData,
  fetchOrgPlans,
  getMonthlyUsage,
  initialized,
  mobileQualityPlans,
  orderItems,
  persistPurchaseData,
  updateProductConfig,
  usagePlans,
  usageSubscriptions,
  user,
}) => {
  useEffect(() => {
    trackEvent(EVENTS.USAGE_PLANS_EMPTY_VISIT, MODE_USAGE);
  }, []);

  const {
    localSidePlansConfig,
    setLocalSidePlansConfig,
    proceedWithSidePlan,
  } = usePlanConfigManager(
    updateProductConfig,
    persistPurchaseData,
  );

  const isUpgrading = usageSubscriptions.size > 0;
  const crumbList = [PARENT_CRUMB];
  if (isUpgrading) {
    crumbList.push({ name: 'Update Plan' });
  }

  return (
    <>
      <Breadcrumbs
        list={crumbList}
        prefixComponent={<BackToPlatformLink />}
      />
      <ContentLoader show={!initialized}>
        <div
          className="bg-gray"
          data-qa="usage-purchase-view"
        >
          <UsagePurchaseSection
            clearPurchaseData={clearPurchaseData}
            fetchOrgPlans={fetchOrgPlans}
            getMonthlyUsage={getMonthlyUsage}
            orderItems={orderItems}
            persistPurchaseData={persistPurchaseData}
            updateProductConfig={updateProductConfig}
            usagePlans={usagePlans}
            usageSubscriptions={usageSubscriptions}
            user={user}
          />
          <div className="mt-5 pb-5 bg-white">
            <div className="max-w-4xl mx-auto">
              <MoreTestingPlans
                apiTestingPlans={apiTestingPlans}
                handleSubscribe={proceedWithSidePlan}
                localPlansConfig={localSidePlansConfig}
                mobileQualityPlans={mobileQualityPlans}
                setLocalPlansConfig={setLocalSidePlansConfig}
              />
            </div>
          </div>
          <div
            className="py-5 bg-gray"
            data-qa="additional-plans"
          >
            <div className="flex flex-row flex-wrap justify-center">
              <EnterprisePlansTile />
            </div>
          </div>
        </div>
      </ContentLoader>
    </>
  );
};

const mapStateToProps = (state) => ({
  ...selectors.purchaseContainerSelector(state),
  apiTestingPlans: plansSelectors.getUnifiedApiTestingPlansSelector(state),
  mobileQualityPlans: plansSelectors.getUnifiedMobileQualityPlansSelector(state),
  usagePlans: plansSelectors.getUsageCreditsPlansSelector(state),
  usageSubscriptions: subscriptionsSelectors.getUsageSubscriptionsSelector(state),
});

const mapDispatchToProps = {
  clearPurchaseData: operations.clearPurchaseData,
  fetchOrgPlans: authActions.getOrgPlans,
  getMonthlyUsage: actions.getMonthlyUsage,
  persistPurchaseData: operations.persistPurchaseData,
  updateProductConfig: actions.updateProductConfig,
};

export { UsagePurchaseContainer };
export default connect(mapStateToProps, mapDispatchToProps)(UsagePurchaseContainer);
